import type { EnhancedDomain } from 'metalworks-domain'
import {
  AnchorPointPosition,
  BendablePlate,
  CircularHole,
  Model,
  StrikePlate,
  StrikePlateShapeType,
  bendData,
} from 'metalworks-domain'
import { match } from 'ts-pattern'

import { assertExistence, isNonNullish } from '@salsita-ai/platform-common/utils'

import { configuratorInterface } from '../configurator-interface'
import { logger } from '../logger'

/**
 * Calculates optimal bend position between holes, preferring positions close to the middle of the plate.
 *
 * @param plateSize - Size of the plate along the bend axis (oldPlate.$sizeX for vertical, oldPlate.$sizeY for horizontal)
 * @param holes - Array of holes on the plate
 * @param bendSafeZone - Safe distance required around the bend
 * @param totalBendLength - Total length that needs to be distributed (2 * bendLengths.innerLength)
 * @param orientation - Bend orientation (Vertical or Horizontal)
 * @returns Object with startInnerLength and endInnerLength
 */
function calculateOptimalBendPosition(
  plateSize: number,
  holes: readonly EnhancedDomain.$Hole[],
  bendSafeZone: number,
  totalBendLength: number,
  orientation: Model.BendOrientation = Model.BendOrientation.Vertical
): { startInnerLength: number; endInnerLength: number } {
  const plateMiddle = plateSize / 2

  // If no holes, place bend in the middle
  if (holes.length === 0) {
    const halfLength = totalBendLength / 2
    return {
      startInnerLength: halfLength,
      endInnerLength: halfLength,
    }
  }

  // Create array of occupied ranges by holes (including their bounding boxes and safe zones)
  // For vertical bends, we check X-axis; for horizontal bends, we check Y-axis
  const occupiedRanges: Array<{ start: number; end: number }> = []

  for (const hole of holes) {
    const holeAbsCoords = hole.$absCoords
    const holeBoundingBox = hole.$boundingBox

    // Skip holes without proper coordinates or bounding box
    if (!holeAbsCoords || !holeBoundingBox?.bottomLeft || !holeBoundingBox?.topRight) {
      continue
    }

    // Use X-axis for vertical bends, Y-axis for horizontal bends
    const holeAbsPos = orientation === Model.BendOrientation.Vertical ? holeAbsCoords.x : holeAbsCoords.y
    const holeBoundingStart =
      orientation === Model.BendOrientation.Vertical ? holeBoundingBox.bottomLeft.x : holeBoundingBox.bottomLeft.y
    const holeBoundingEnd =
      orientation === Model.BendOrientation.Vertical ? holeBoundingBox.topRight.x : holeBoundingBox.topRight.y

    // Calculate the actual occupied range including the hole's bounding box
    const holeStart = holeAbsPos + holeBoundingStart - bendSafeZone
    const holeEnd = holeAbsPos + holeBoundingEnd + bendSafeZone

    occupiedRanges.push({ start: holeStart, end: holeEnd })
  }

  // Sort ranges by start position
  occupiedRanges.sort((a, b) => a.start - b.start)

  // Merge overlapping ranges
  const mergedRanges: Array<{ start: number; end: number }> = []
  for (const range of occupiedRanges) {
    const lastRange = mergedRanges[mergedRanges.length - 1]
    if (mergedRanges.length === 0 || !lastRange || lastRange.end < range.start) {
      mergedRanges.push(range)
    } else {
      lastRange.end = Math.max(lastRange.end, range.end)
    }
  }

  // Find available gaps between holes where bend could be placed
  const availableGaps: Array<{ start: number; end: number; size: number }> = []

  // Gap before first hole
  const firstRange = mergedRanges[0]
  if (mergedRanges.length > 0 && firstRange && firstRange.start > 0) {
    const gapStart = 0
    const gapEnd = firstRange.start
    availableGaps.push({ start: gapStart, end: gapEnd, size: gapEnd - gapStart })
  }

  // Gaps between holes
  for (let i = 0; i < mergedRanges.length - 1; i++) {
    const currentRange = mergedRanges[i]
    const nextRange = mergedRanges[i + 1]
    if (currentRange && nextRange) {
      const gapStart = currentRange.end
      const gapEnd = nextRange.start
      if (gapEnd > gapStart) {
        availableGaps.push({ start: gapStart, end: gapEnd, size: gapEnd - gapStart })
      }
    }
  }

  // Gap after last hole
  const lastRange = mergedRanges[mergedRanges.length - 1]
  if (mergedRanges.length > 0 && lastRange && lastRange.end < plateSize) {
    const gapStart = lastRange.end
    const gapEnd = plateSize
    availableGaps.push({ start: gapStart, end: gapEnd, size: gapEnd - gapStart })
  }

  // If no holes occupy any space, place in middle
  if (mergedRanges.length === 0) {
    const halfLength = totalBendLength / 2
    return {
      startInnerLength: halfLength,
      endInnerLength: halfLength,
    }
  }

  // Find the best gap - prefer gaps that contain or are closest to the plate middle
  let bestGap: { start: number; end: number; size: number } | null = null
  let bestScore = -Infinity

  for (const gap of availableGaps) {
    // Check if gap is large enough for the bend (with some minimum clearance)
    const minGapSize = bendSafeZone * 2 // Minimum gap size needed
    if (gap.size < minGapSize) {
      continue
    }

    // Calculate score based on proximity to middle and gap size
    const gapMiddle = (gap.start + gap.end) / 2
    const distanceFromPlateMiddle = Math.abs(gapMiddle - plateMiddle)

    // Prefer gaps closer to the middle, with larger gaps as tiebreaker
    const score = -distanceFromPlateMiddle + gap.size * 0.1

    if (score > bestScore) {
      bestScore = score
      bestGap = gap
    }
  }

  // If we found a suitable gap, position the bend within it
  if (bestGap) {
    const gapMiddle = (bestGap.start + bestGap.end) / 2

    // Try to center the bend in the gap, but ensure it doesn't exceed plate bounds
    const idealBendPosition = Math.max(0, Math.min(plateSize, gapMiddle))

    // Calculate start and end lengths based on the bend position
    const startLength = idealBendPosition
    const endLength = plateSize - idealBendPosition

    // Ensure the total length matches the required bend length
    const totalAvailable = startLength + endLength
    const ratio = totalBendLength / totalAvailable

    return {
      startInnerLength: startLength * ratio,
      endInnerLength: endLength * ratio,
    }
  }

  // Fallback: if no suitable gap found, place in the middle (original behavior)
  const halfLength = totalBendLength / 2
  return {
    startInnerLength: halfLength,
    endInnerLength: halfLength,
  }
}

/**
 * This transformer adds or removes a bend from the plate when the hasBend attribute is modified.
 *
 * This is done to simplify the UI and make it easier to add and remove bend.
 * There can be only one bend per plate currently and its properties are configured in the "plate" configuration.
 *
 * When adding support for multiple bends in the future, this will need to be updated.
 * Using recipes would make more sense in that case.
 */
export const toggleBendablePlateBendTransformer = configuratorInterface.transformerBuilder.when
  .entity(BendablePlate.types)
  .modified({ atFields: ['hasBend'] })
  .then.applySideEffect(ctx => {
    const addBend = ctx.update.stateDelta.hasBend
    const oldPlate = ctx.getOld$Root()

    const oldBend = oldPlate.$customBend

    if (addBend) {
      if (oldBend) {
        return []
      }

      const bendTool = bendData.d90.getTool(oldPlate.thickness)
      if (!bendTool) {
        return []
      }

      const bendLengths = bendData.d90.calculateArmLength(
        oldPlate.$sizeX,
        oldPlate.thickness,
        bendTool.innerRadius,
        oldPlate.rawMaterial
      )

      // Safe distance is 3 times the inner radius on each side
      const bendSafeZone = bendTool.innerRadius * 3
      const holes = oldPlate.$holes

      // Calculate optimal bend position between holes (vertical orientation by default)
      const { startInnerLength, endInnerLength } = calculateOptimalBendPosition(
        oldPlate.$sizeX,
        holes,
        bendSafeZone,
        bendLengths.innerLength * 2,
        Model.BendOrientation.Vertical
      )

      // Check if any holes are assigned to "center", "top" or "bottom" anchor points of the plate.
      // When adding a vertical bend, these anchor points should be reassigned to corner anchor points.
      const plateAnchorPoints = oldPlate.$anchorPoints
      const centerAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'center')
      const topAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'top')
      const bottomAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'bottom')
      const topLeftAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'topLeft')
      const topRightAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'topRight')
      const bottomLeftAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'bottomLeft')
      const bottomRightAnchorPoint = plateAnchorPoints.find(ap => ap.position === 'bottomRight')

      const anchorPointsToReassign = [centerAnchorPoint?.id, topAnchorPoint?.id, bottomAnchorPoint?.id].filter(
        (id): id is Model.AnchorPoint['id'] => id !== undefined
      )

      const holesToReassign = holes.filter(hole => anchorPointsToReassign.includes(hole.anchorPointToId))

      const holeUpdates = holesToReassign
        .map(hole => {
          const currentOriginAnchorPoint = assertExistence(hole.$anchorPointTo)
          const holeAbsX = hole.$absCoords.x
          const holeAbsY = hole.$absCoords.y

          // Determine if hole is closer to left or right edge
          const plateCenterX = oldPlate.$sizeX / 2
          const plateCenterY = oldPlate.$sizeY / 2
          const isCloserToLeft = holeAbsX < plateCenterX
          const isCloserToTop = holeAbsY > plateCenterY

          // Map old anchor point position to new one (always to a corner)
          let newAnchorPoint: EnhancedDomain.$AnchorPoint | undefined
          if (currentOriginAnchorPoint.position === 'center') {
            // For center, choose the closest corner based on both X and Y position
            if (isCloserToTop && isCloserToLeft) {
              newAnchorPoint = topLeftAnchorPoint
            } else if (isCloserToTop && !isCloserToLeft) {
              newAnchorPoint = topRightAnchorPoint
            } else if (!isCloserToTop && isCloserToLeft) {
              newAnchorPoint = bottomLeftAnchorPoint
            } else {
              newAnchorPoint = bottomRightAnchorPoint
            }
          } else if (currentOriginAnchorPoint.position === 'top') {
            newAnchorPoint = isCloserToLeft ? topLeftAnchorPoint : topRightAnchorPoint
          } else if (currentOriginAnchorPoint.position === 'bottom') {
            newAnchorPoint = isCloserToLeft ? bottomLeftAnchorPoint : bottomRightAnchorPoint
          }

          if (!newAnchorPoint) {
            logger.error('toggleBendablePlateBendTransformer: Could not find new anchor point', {
              holeId: hole.id,
              currentPosition: currentOriginAnchorPoint.position,
              isCloserToLeft,
            })
            return null
          }

          // Calculate delta similar to handleOriginAnchorPointClick
          const deltaX = currentOriginAnchorPoint.$absCoords.x - newAnchorPoint.$absCoords.x
          const deltaY = currentOriginAnchorPoint.$absCoords.y - newAnchorPoint.$absCoords.y

          return configuratorInterface.updates.DomainEntityModification({
            entityId: hole.id,
            updateTag: 'reassignHoleAnchorPointForBend',
            stateDelta: {
              anchorPointFromId: hole.anchorPointFromId,
              anchorPointToId: newAnchorPoint.id,
              x: hole.x + deltaX,
              y: hole.y + deltaY,
              ...(CircularHole.is(hole) ? { radius: hole.radius } : {}),
            },
          })
        })
        .filter(
          (update): update is ReturnType<typeof configuratorInterface.updates.DomainEntityModification> =>
            update !== null
        )

      return [
        configuratorInterface.updates.DomainEntityCreation({
          parentId: oldPlate.id,
          entity: {
            id: ctx.getIdSequence().nextId('CustomBend') as Model.CustomBend['id'],
            type: 'CustomBend',
            startInnerLength,
            endInnerLength,
            orientation: Model.BendOrientation.Vertical,
            direction: Model.BendDirection.Back,
          },
          updateTag: 'toggleBendAdd',
        }),
        ...holeUpdates,
      ]
    } else {
      if (!oldBend) {
        return []
      }

      return [
        configuratorInterface.updates.EntityDeletion({
          entityId: oldBend.id,
          updateTag: 'toggleBendRemove',
        }),
      ]
    }
  })
  .build('Add or remove bend')

export const updateArmLengthsTransformer = configuratorInterface.transformerBuilder.when
  .entity([Model.CustomBend.type])
  .modified({ atFields: ['orientation'] })
  .then.applySideEffect(ctx => {
    const oldPlate = ctx.getOld$Root()
    const orientation = ctx.update.stateDelta.orientation
    const size = orientation === Model.BendOrientation.Horizontal ? oldPlate.$sizeY : oldPlate.$sizeX
    const oldBend = oldPlate.$customBend

    if (!oldBend) {
      logger.error('updateArmLengthsTransformer: No bend found for plate')
      return []
    }

    const bendTool = bendData.d90.getTool(oldPlate.thickness)
    if (!bendTool) {
      logger.error('updateArmLengthsTransformer: No bend tool found for thickness', { thickness: oldPlate.thickness })
      return []
    }

    const armLength = bendData.d90.calculateArmLength(
      size,
      oldPlate.thickness,
      bendTool.innerRadius,
      oldPlate.rawMaterial
    )

    // Safe distance is 3 times the inner radius on each side
    const bendSafeZone = bendTool.innerRadius * 3
    const holes = oldPlate.$holes

    // Calculate optimal bend position between holes based on new orientation
    const { startInnerLength, endInnerLength } = calculateOptimalBendPosition(
      size,
      holes,
      bendSafeZone,
      armLength.innerLength * 2,
      orientation
    )

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: oldBend.id,
        stateDelta: {
          startInnerLength,
          endInnerLength,
        },
        updateTag: 'updateArmLengths',
      }),
    ]
  })
  .build('Update arm lengths when orientation changes')

const handleBendLengthUpdate = (
  oldPlate: EnhancedDomain.$Plate,
  delta: { startInnerLength?: number; endInnerLength?: number }
) => {
  const oldBend = oldPlate.$customBend
  if (!oldBend) {
    return []
  }

  const bendTool = bendData.d90.getTool(oldPlate.thickness)
  if (!bendTool) {
    return []
  }

  const isHorizontal = oldBend.orientation === Model.BendOrientation.Horizontal
  const lengths = {
    startInnerLength: oldBend.startInnerLength,
    endInnerLength: oldBend.endInnerLength,
    ...(delta.startInnerLength ? { startInnerLength: delta.startInnerLength } : {}),
    ...(delta.endInnerLength ? { endInnerLength: delta.endInnerLength } : {}),
  }

  const flatLength = bendData.d90.calculateFlatLength(
    lengths.startInnerLength,
    lengths.endInnerLength,
    oldPlate.thickness,
    bendTool.innerRadius,
    oldPlate.rawMaterial
  )

  const stateDelta = match(oldPlate)
    .when(Model.SquarePlate.is, () => ({ sideLength: flatLength }))
    .when(Model.RoundPlate.is, () => ({ diameter: flatLength }))
    .when(Model.RectangularPlate.is, () =>
      isHorizontal ? { verticalLength: flatLength } : { horizontalLength: flatLength }
    )
    .otherwise(() => null)

  if (!stateDelta) {
    return []
  }

  return [
    configuratorInterface.updates.DomainEntityModification({
      entityId: oldPlate.id,
      stateDelta,
      updateTag: 'updatePlateLength',
    }),
  ]
}

export const updateFlatPlateLengthOnBendStartLengthChangeTransformer = configuratorInterface.transformerBuilder.when
  .entity([Model.CustomBend.type])
  .modified({ atFields: ['startInnerLength'] })
  .then.applySideEffect(ctx => handleBendLengthUpdate(ctx.getNew$Root(), ctx.update.stateDelta))
  .build('Update flat plate length when bend start length changes')

export const updateFlatPlateLengthOnBendEndLengthChangeTransformer = configuratorInterface.transformerBuilder.when
  .entity([Model.CustomBend.type])
  .modified({ atFields: ['endInnerLength'] })
  .then.applySideEffect(ctx => handleBendLengthUpdate(ctx.getNew$Root(), ctx.update.stateDelta))
  .build('Update flat plate length when bend end length changes')

export const updatePlateMaterialOnBendChangeTransformer = configuratorInterface.transformerBuilder.when
  .entity(Model.Plate.types)
  .violated(['hasBendTool'])
  .then.applySideEffect(ctx => {
    const plate = ctx.getNew$Root()

    const firstUpdate = ctx.updates[0]
    if (
      !firstUpdate ||
      // Ignore thickness change - these shouldn't be fixed. We need to fix
      // only other changes which result in material not being available.
      (firstUpdate.kind === 'modifyDomainEntity' &&
        (firstUpdate.stateDelta as Partial<Model.Plate>).thickness !== undefined)
    ) {
      return []
    }

    const bendType = Model.StrikePlate.is(plate) ? plate.bendType : Model.BendType.d90
    const materials = plate.$customMaterials.filter(
      m => m.rawMaterial === plate.rawMaterial && m.finish === plate.finish && bendData[bendType].getTool(m.thickness)
    )

    const fallbackMaterial = materials[0]
    if (!fallbackMaterial) {
      return []
    }

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: plate.id,
        stateDelta: { thickness: fallbackMaterial.thickness },
        updateTag: 'updatePlateMaterialOnBendChange',
      }),
    ]
  })
  .build('Update plate material when bend changes')

export const fixTStrikeInsufficientSizeTransformer = configuratorInterface.transformerBuilder.when
  .entity([Model.StrikePlate.type])
  .violated(['strikePlateWidthSmallerThanTongueWidth'])
  .then.applySideEffect(ctx => {
    const plate = ctx.getNew$Root()

    if (!StrikePlate.is(plate)) {
      return []
    }

    if (plate.shapeType === StrikePlateShapeType.DStrike) {
      return []
    }

    const minTongueForCurrentBend = plate.$minTongueHorizontalLength
    const targetTongueWidth = Math.max(plate.tongueHorizontalLength, minTongueForCurrentBend)
    const minimumHorizontalLength = targetTongueWidth + plate.$minLength
    const targetHorizontalLength = Math.max(plate.horizontalLength, minimumHorizontalLength)

    const stateDelta = {
      ...(targetTongueWidth !== plate.tongueHorizontalLength ? { tongueHorizontalLength: targetTongueWidth } : {}),
      ...(targetHorizontalLength !== plate.horizontalLength ? { horizontalLength: targetHorizontalLength } : {}),
    } satisfies Partial<Model.StrikePlate>

    if (Object.keys(stateDelta).length === 0) {
      return []
    }

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: plate.id,
        stateDelta,
        updateTag: 'fixTStrikeInsufficientSize',
      }),
    ]
  })
  .build('Fix T-Strike insufficient size')

const mirrorAnchorPointPosition = {
  [AnchorPointPosition.topLeft]: AnchorPointPosition.topRight,
  [AnchorPointPosition.topRight]: AnchorPointPosition.topLeft,
  [AnchorPointPosition.bottomLeft]: AnchorPointPosition.bottomRight,
  [AnchorPointPosition.bottomRight]: AnchorPointPosition.bottomLeft,
  [AnchorPointPosition.top]: AnchorPointPosition.top,
  [AnchorPointPosition.bottom]: AnchorPointPosition.bottom,
  [AnchorPointPosition.left]: AnchorPointPosition.right,
  [AnchorPointPosition.right]: AnchorPointPosition.left,
  [AnchorPointPosition.center]: AnchorPointPosition.center,
}

export const flipAnchorableShapePositionTransformer = configuratorInterface.transformerBuilder.when
  .entity([StrikePlate.type])
  .modified({ atFields: ['tonguePosition'] })
  .then.applySideEffect(ctx => {
    const plate = ctx.getOld$Root() as EnhancedDomain.$StrikePlate
    const tonguePosition = ctx.update.stateDelta.tonguePosition

    if (plate.tonguePosition === tonguePosition) {
      return []
    }

    const tongueBend = plate.$tongueBend
    if (!tongueBend) {
      return []
    }

    const updates = plate.$holes
      .map(h => {
        const currentOriginAnchorPoint = h.$anchorPointTo
        const currentOrigin = currentOriginAnchorPoint.$shape

        const flippedAnchorPointPosition = mirrorAnchorPointPosition[currentOriginAnchorPoint.position]
        const flippedAnchorPoint = currentOrigin.$anchorPoints.find(ap => ap.position === flippedAnchorPointPosition)

        if (!flippedAnchorPoint) {
          return null
        }

        return configuratorInterface.updates.DomainEntityModification({
          entityId: h.id,
          stateDelta: {
            x: h.x * -1,
            anchorPointToId: flippedAnchorPoint.id,
          },
          updateTag: 'flipAnchorableShapePosition',
        })
      })
      .filter(isNonNullish)

    return updates
  })
  .build('Flip anchorable shape position when tongue position changes')
